import { useCallback, useEffect } from 'react';
import {
  ResourceShiftTotalsMap,
  ResourceViolationItem,
  SchedulerDayColumn,
  ServiceRulesViolations,
} from '@lp/features/Scheduler/types';
import { baseRowClassname } from '@lp/features/Scheduler/constants';
import { getIsHighlightedCell, getScheduleDayViolations } from '@lp/features/Scheduler/utils';
import { DayColumnHeader, UserProfileCell, UserProfileHeader } from '@lp/features/Scheduler/components';
import { useDayColumns, useSchedulerTableAutoScroll, useSchedulerUserRows } from '@lp/features/Scheduler/hooks';
import { OMS_SHIFT_TYPE_SELECT_LIST } from '@lp/features/Scheduler/SchedulerOMS/constants';
import { useOMSSchedulerFiltersStore, useOMSSchedulerHighlightStore } from '@lp/core/store/scheduler';
import { useOMSShiftSiblingMaps } from '@lp/features/Scheduler/SchedulerOMS/components/OMSSchedulerTable/hooks';
import {
  OMSDayCell,
  OMSOnCallTotalsSubTable,
  OMSResourceShiftTotalsTable,
} from '@lp/features/Scheduler/SchedulerOMS/components/OMSSchedulerTable/components';
import {
  getIsLastOnCallSlotInDay,
  getOMSShiftSetData,
  OMSShiftOptionType,
  omsUserRowsFilterComparator,
} from '@lp/features/Scheduler/SchedulerOMS';
import {
  OMSResource,
  OMSResourcesWithShifts,
  SetOMSShiftData,
  OMSShiftSelectPayload,
} from '@/api/apis/resources/schedule';

type OMSSchedulerTableProps = {
  isEditable: boolean;
  canResetShifts: boolean;
  canUnblockShifts?: boolean;
  selectedDate: Date;
  daysInterval: Date[];
  userList: OMSResource[];
  shiftsData: OMSResourcesWithShifts;
  onShiftChange: (resourceId: string, day: string, payload?: SetOMSShiftData) => void;
  onUnblockShift?: (resourceId: string, day: string, scheduleId: string) => void;
  withOnCallTotals?: boolean;
  shiftTotalsMap?: ResourceShiftTotalsMap<OMSShiftOptionType>;
  dayViolations?: ServiceRulesViolations;
  resourceViolations?: Record<string, ResourceViolationItem>;
};

export const OMSSchedulerTable = ({
  isEditable,
  canResetShifts,
  canUnblockShifts,
  selectedDate,
  daysInterval,
  userList,
  shiftsData,
  shiftTotalsMap,
  onShiftChange,
  onUnblockShift,
  withOnCallTotals,
  dayViolations,
  resourceViolations,
}: OMSSchedulerTableProps) => {
  const filters = useOMSSchedulerFiltersStore();
  const highlightValue = useOMSSchedulerHighlightStore.use.value();

  const { scrollContainerRef, rowRefCallback, columnRefCallback, scrollToCell } =
    useSchedulerTableAutoScroll(selectedDate);
  const dayCols = useDayColumns(daysInterval, selectedDate);
  const siblingMaps = useOMSShiftSiblingMaps(dayCols, userList, shiftsData);
  const { userRows, sortAsc, toggleSortAsc, daysFilters, setDayFilter } = useSchedulerUserRows({
    dayCols,
    userList,
    shiftsData,
    filters,
    filterComparator: omsUserRowsFilterComparator,
  });

  useEffect(() => {
    if (highlightValue) {
      const resourceId = highlightValue.resourceId ?? userRows[0]?.id;
      const day = highlightValue.day ?? (dayCols.find((col) => col.isCurrentMonth)?.date as Date);

      scrollToCell(resourceId, day, true);
    }
  }, [dayCols, highlightValue, scrollToCell, userRows]);

  const getResettableSlotGetter = useCallback(
    (resourceId: string, day: string) => (slotIndex: number) => {
      if (canResetShifts) return true;
      return !getIsLastOnCallSlotInDay(shiftsData, resourceId, day, slotIndex);
    },
    [canResetShifts, shiftsData],
  );

  const getShiftChangeHandler = useCallback(
    (resourceId: string, day: string) => (shiftPayload?: OMSShiftSelectPayload) => {
      if (!shiftPayload) {
        onShiftChange(resourceId, day);
        return;
      }

      const { resource } = shiftsData[resourceId];
      const changeData = getOMSShiftSetData(shiftPayload, resource);

      onShiftChange(resourceId, day, changeData);
    },
    [onShiftChange, shiftsData],
  );

  const getUnblockHandler = useCallback(
    (resourceId: string, day: string) => () => {
      const shift = shiftsData[resourceId]?.days[day];
      if (shift && onUnblockShift) {
        onUnblockShift(resourceId, day, shift.scheduleId);
      }
    },
    [shiftsData, onUnblockShift],
  );

  const renderShiftCell = useCallback(
    (column: SchedulerDayColumn, resourceId: string) => {
      const day = column.key;
      const shift = shiftsData[resourceId].days[day];
      const siblingData = siblingMaps[resourceId].get(day);
      const handler = getShiftChangeHandler(resourceId, day);
      const getResettableSlot = getResettableSlotGetter(resourceId, day);
      const highlight = getIsHighlightedCell(resourceId, column.date, highlightValue);

      return (
        <OMSDayCell
          key={column.key}
          column={column}
          shiftData={shift}
          siblingData={siblingData}
          isEditable={isEditable}
          canReset={canResetShifts}
          canUnblock={canUnblockShifts}
          highlight={highlight}
          onShiftChange={handler}
          onUnblock={canUnblockShifts ? getUnblockHandler(resourceId, day) : undefined}
          getResettableSlot={getResettableSlot}
        />
      );
    },
    [
      shiftsData,
      siblingMaps,
      getShiftChangeHandler,
      getUnblockHandler,
      getResettableSlotGetter,
      highlightValue,
      isEditable,
      canResetShifts,
      canUnblockShifts,
    ],
  );

  return (
    <div className="relative">
      <div ref={scrollContainerRef} className="w-full overflow-auto">
        <table aria-label="OMS Schedule table" className="relative border-separate border-spacing-0 pb-4 pr-4">
          <thead>
            <tr className="h-10 text-left [&>th]:px-3 [&>th]:text-s-sb first:[&>th]:rounded-s-lg last:[&>th]:rounded-e-lg">
              <UserProfileHeader sortAsc={sortAsc} toggleSortAsc={toggleSortAsc} />
              {dayCols.map((column) => (
                <DayColumnHeader
                  ref={(el) => columnRefCallback(el, column.date)}
                  key={column.key}
                  column={column}
                  withFilter={filters.statusFilters}
                  filterOptionList={OMS_SHIFT_TYPE_SELECT_LIST}
                  violations={getScheduleDayViolations(column.key, dayViolations)}
                  selectedKeys={daysFilters[column.key]}
                  onSelectedKeysChange={(shiftTypes) => setDayFilter(column.key, shiftTypes)}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {userRows.map((user) => (
              <tr key={user.id} ref={(el) => rowRefCallback(user.id, el)} className={baseRowClassname}>
                <UserProfileCell key="user" user={user} violation={resourceViolations?.[user.id]} />
                {dayCols.map((column) => renderShiftCell(column, user.id))}
              </tr>
            ))}
            {withOnCallTotals && (
              <OMSOnCallTotalsSubTable dayCols={dayCols} userList={userRows} shiftsData={shiftsData} />
            )}
          </tbody>
        </table>
      </div>
      {filters.shiftTotals && <OMSResourceShiftTotalsTable userRows={userRows} shiftTotalsMap={shiftTotalsMap} />}
    </div>
  );
};
